import {
  handleDownloadPdfReport,
  handleEditFindings,
  handleEditPhysicianInterpretation,
  handleMarkInboxReviewed,
  handleSaveSignatureDateToReport,
  handleUploadPdfReport,
} from '@/entities/inbox';
import { handleSaveSignature, useUserStore } from '@/entities/user';
import { useSupportStore } from '@/entities/support';
import CeraProRegularFont from '@/shared/ui/assets/fonts/ceraPro/CeraPro-Regular.ttf';
import CeraProMediumFont from '@/shared/ui/assets/fonts/ceraPro/CeraPro-Medium.ttf';
import TahomaRegularFont from '@/shared/ui/assets/fonts/tahoma/Tahoma-Regular.ttf';
import { EMITTER_CONSTANTS, LOADING_TYPES } from '@/shared/config';
import { STUDY_TYPES } from '@/entities/study';
import { useLoadingStore } from '@/shared/model';
import {
  dataURIToUInt8Array,
  handleDownloadFile,
  isVersionAtLeast,
  uploadFileToUrl,
  auth,
  emitter,
  generateFullName,
  showMessageError,
  showMessageSuccess,
} from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import fontkit from '@pdf-lib/fontkit';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { PDFDocument, rgb } from 'pdf-lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { checkAddPhysicianInterpretation } from '../lib/editReportModal';
import { checkNewNotiEventReport } from '../lib/inbox';
import {
  ADD_SIGNATURE_TIME_TOTAL_LIMIT,
  INITIAL_TIME_DIFF_OBJECT,
  REPORT_TYPE,
  REPORT_VERSION,
} from '../config/inboxDetails';
import {
  checkShouldTrackAddSignatureTime,
  drawFindingOnEventStrip,
  getModifyPdfReportOptions,
  getReportFileName,
  handleUpdateInterpretationPdf,
  truncateFindingsText,
} from '../lib/inboxDetails';
import type {
  InboxDetailsProps,
  InboxDetailsState,
  SavePdfReportParams,
} from './inboxDetails.types';

/**
 * Drives the inbox report details view where a physician reads, annotates, signs, saves and
 * downloads a report PDF. Derives the report kind flags and findings from the report, marks it read
 * and raises the facility escalation popup on mount, loads the stamping font, turns the PDF bytes
 * into a viewer URL, and exposes the comment, signature, save, download and modal handlers.
 *
 * @param props - Report data plus the back, report update, verified-report, after-reading and after-saving callbacks and the instant and verified report flags.
 * @returns An object carrying the report data, view state, display flags, PDF URL ref and every view handler.
 */
const useInboxDetails = ({
  data,
  handleClickBack,
  updateClickedReport,
  isInstantReport: isInstantReportProp,
  hasVerifiedReport,
  handleViewVerifiedReportClick: handleViewVerifiedReportClickProp,
  handleAfterReadingReport,
  handleAfterSavingPdfReport,
}: Pick<
  InboxDetailsProps,
  | 'data'
  | 'handleClickBack'
  | 'updateClickedReport'
  | 'isInstantReport'
  | 'hasVerifiedReport'
  | 'handleViewVerifiedReportClick'
  | 'handleAfterReadingReport'
  | 'handleAfterSavingPdfReport'
>) => {
  const navigate = useNavigate();

  /**
   * The facility list held in the support store, used to look up arrears for the report's facility. Selected from useSupportStore, whose supportFacilities value is replaced by setSupportFacilities.
   *
   * @returns Facilities currently registered as supported.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  const meRedux = useUserStore((s) => s.me);

  /**
   * The global loading-text setter taken from the loading store, used to show and clear the download indicator. Selected from useLoadingStore as its setLoadingText action.
   *
   * @returns Action that sets or clears the page loading text.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  const [fontBytes, setFontBytes] = useState<ArrayBuffer | undefined>(undefined);

  const hasSignature = useRef(data?.hasSignature);
  const physInterpretation = useRef(data?.physicianInterpretation || '');
  const currentUrl = useRef<string>('');
  const isInitData = useRef(false);

  const isEoUReport = data.type === 'Study';

  // For customer environment
  const totalStudyDuration = dayjs(data.stop).diff(data.start, 'days', true);
  // *: For testing (CP-2.24.2)
  // const totalStudyDuration = dayjs(props.data.stop).diff(props.data.start, 'm', true);

  const shouldDisplayDiagnosticYieldAlert =
    auth.isClinicPhysician() && isEoUReport && totalStudyDuration < 10;

  const shouldDisplayInstantReportAlert = isInstantReportProp;

  const isRendering = data?.status === 'Rendering' || data?.isRenderingBackground;

  const [state, setState] = useMergeState<InboxDetailsState>({
    modalHeader: '',
    isOpenEditReportModal: false,
    isUploadedAiReport: false,
    isUploadedArcsReport: false,
    isInstantReport: false,
    isNotificationReport: false,
    isEventReport: false,
    isInterimReport: false,
    isEouReport: false,
    isMctReport: false,
    isMctOrMctPeek: false,
    isMctOnly: false,
    isCardiacEvent: false,
    pdfURL: '',
    physicianInterpretation: '',
    signatureBase64String: '',
    isSaving: false,
    pdfArrayBuffer: undefined,
    arrayBufferChange: new Date().getTime(),
    isReviewing: false,
    hasClosedDiagnosticYieldAlert: false,
    openUnsavedChangesFindingsModal: false,
    openVerifiedReportModal: false,
    openFailedLoadingFontsModal: false,
  });

  const addSignatureTrackingTime = useRef({ ...INITIAL_TIME_DIFF_OBJECT });

  const isNewFormat = !!data?.isNewFormat;
  const formatVersion = data?.formatVersion;
  const aiFormatVersion = data?.aiFormatVersion;
  const isNewNotiEventReport = checkNewNotiEventReport(data);
  /**
   * Whether the open report is an Event report on report format 2.0.2 or later. Combines the REPORT_TYPE.EVENT type check with isVersionAtLeast against REPORT_VERSION.V_2_0_2.
   *
   * @returns True for event reports at format 2.0.2 or above.
   */
  const isNewEventVer200 =
    data.type === REPORT_TYPE.EVENT && isVersionAtLeast(formatVersion, REPORT_VERSION.V_2_0_2);

  /**
   * Whether the interpretation currently in the findings editor differs from the last persisted value. Memoized deep comparison of state.physicianInterpretation against the physInterpretation ref, recomputed whenever either side changes.
   *
   * @returns True when the findings text has unsaved edits.
   */
  const hasChangedPhysicianInterpretation = useMemo(
    () => !_.isEqual(state.physicianInterpretation, physInterpretation.current),
    [state.physicianInterpretation, physInterpretation.current],
  );

  /**
   * Opens or closes the edit report modal on the report details page. Inverts isOpenEditReportModal and keeps the previous header when no modalHeader is given.
   *
   * @param modalHeader - Header text for the modal; empty keeps the current one.
   * @returns Nothing.
   */
  const toggleDisplayEditReportModal = (modalHeader = '') => {
    setState({
      isOpenEditReportModal: !state.isOpenEditReportModal,
      modalHeader: modalHeader || state.modalHeader,
    });
  };

  /**
   * Persists an edited report PDF together with its findings and signature metadata. Uploads the pdf bytes to a signed url, saves changed findings through handleEditFindings or handleEditPhysicianInterpretation, stores the signature date when requested, uploads the report, then updates the clicked report, shows a success message and runs the callback; every step's duration lands in addSignatureTrackingTime and failures reset isSaving.
   *
   * @param physicianInterpretation - Findings text to save; defaults to the current state value.
   * @param signatureBase64String - Signature image data to record; defaults to the current state value.
   * @param currentSignTime - ISO signing timestamp, or null when no signature was added.
   * @param currentDateTimeText - Formatted signature date drawn onto the PDF.
   * @param shouldUpdateSignatureDate - Whether the signature date is stored on the report.
   * @param pdfArrayBuffer - Bytes of the modified PDF to upload.
   * @param callback - Invoked after the save succeeded.
   * @returns Resolves once the report is saved or the error was shown.
   */
  const handleSavePdfReport = async ({
    physicianInterpretation = state.physicianInterpretation,
    signatureBase64String = state.signatureBase64String,
    currentSignTime = null,
    currentDateTimeText = '',
    shouldUpdateSignatureDate = false,
    pdfArrayBuffer = undefined,
    callback,
  }: SavePdfReportParams) => {
    try {
      const startTimeUploadPdfReportToUrl = dayjs();
      const pdfReportUrl = await uploadFileToUrl([pdfArrayBuffer], 'pdf');
      const endTimeUploadPdfReportToUrl = dayjs();
      addSignatureTrackingTime.current.uploadPdfReportToUrl = endTimeUploadPdfReportToUrl.diff(
        startTimeUploadPdfReportToUrl,
        'ms',
        true,
      );

      const uploadPdfReportVariables = {
        studyFid: data.studyFid,
        type: data.type,
        reportUrl: pdfReportUrl as string | undefined,
        hasSignature: hasSignature.current,
        isNewFormat: data?.isNewFormat,
      };
      if (_.includes(['Study', 'Interim'], data.type)) {
        _.assign(uploadPdfReportVariables, { reportId: data.id });
      }
      if (data.reportId) {
        if (data.start && data.stop) {
          _.assign(uploadPdfReportVariables, {
            start: dayjs(data.start).valueOf(),
            stop: dayjs(data.stop).valueOf(),
          });
        }
      }
      if (data.eventFid) {
        _.assign(uploadPdfReportVariables, { eventFid: data.eventFid });
      }

      if (hasChangedPhysicianInterpretation) {
        physInterpretation.current = physicianInterpretation;
        const startTimeEditPhysicianInterpretation = dayjs();
        if (isNewNotiEventReport) {
          await handleEditFindings(data.id, physicianInterpretation);
        } else {
          await handleEditPhysicianInterpretation(data.id, physicianInterpretation);
        }
        const endTimeEditPhysicianInterpretation = dayjs();
        addSignatureTrackingTime.current.editPhysicianInterpretation =
          endTimeEditPhysicianInterpretation.diff(startTimeEditPhysicianInterpretation, 'ms', true);
      }
      if (currentSignTime && shouldUpdateSignatureDate) {
        const startTimeSaveSignatureDateToReport = dayjs();
        await handleSaveSignatureDateToReport(data.id, currentSignTime, currentDateTimeText);
        const endTimeSaveSignatureDateToReport = dayjs();
        addSignatureTrackingTime.current.saveSignatureDateToReport =
          endTimeSaveSignatureDateToReport.diff(startTimeSaveSignatureDateToReport, 'ms', true);
      }
      const startTimeUploadPdfReport = dayjs();
      await handleUploadPdfReport(uploadPdfReportVariables);
      const endTimeUploadPdfReport = dayjs();
      addSignatureTrackingTime.current.uploadPdfReport = endTimeUploadPdfReport.diff(
        startTimeUploadPdfReport,
        'ms',
        true,
      );

      updateClickedReport({
        physicianInterpretation: physicianInterpretation ?? undefined,
        reportFindings: physicianInterpretation ?? undefined,
        signatureBase64String: signatureBase64String ?? undefined,
        pdfArrayBuffer,
      });
      showMessageSuccess({ content: t`Your changes have been successfully saved` });

      if (_.isFunction(callback)) {
        callback();
      }
    } catch (error) {
      console.error('Failed to save PDF report: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
      setState({ isSaving: false });
    }
  };

  /**
   * Downloads the PDF of the opened inbox report. Shows the downloading loader, requests a signed url through handleDownloadPdfReport with the report, study and event identifiers, saves it under the name from getReportFileName, and always clears the loader while errors surface as a message.
   *
   * @returns Resolves once the download finished or failed.
   */
  const downloadPdfReport = async () => {
    try {
      loadingPageAction(LOADING_TYPES.DOWNLOADING);
      const result = await handleDownloadPdfReport({
        id: data?.id,
        studyFid: data?.studyFid,
        reportType: data?.type,
        reportId: data?.reportId,
        eventFid: data?.eventFid,
      });
      await handleDownloadFile(result?.data?.downloadPdfReport?.url, getReportFileName(data));
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : '' });
    } finally {
      loadingPageAction();
    }
  };

  /**
   * Adds the physician's findings comment to page one of the report PDF and saves it. Aborts with the font-failure modal without fontBytes and with an error for an encrypted document, otherwise embeds the custom font, hides the default comments with a rectangle from getModifyPdfReportOptions, draws the blank-line-collapsed text (truncated for new notification/event reports), stamps event strips when applicable and passes the serialized bytes to handleSavePdfReport.
   *
   * @param physicianInterpretation - Findings comment to draw onto the report.
   * @returns Resolves once the edited PDF was handed to the save step.
   */
  const handleClickAddComment = async (physicianInterpretation: string) => {
    try {
      if (!fontBytes) {
        setState({ openFailedLoadingFontsModal: true });
        return;
      }

      setState({ isSaving: true });

      // *: Load a PDFDocument from the existing PDF bytes
      // pdfArrayBuffer is guaranteed set: the Add-comment action is only reachable once the report PDF has loaded
      const pdfDoc = await PDFDocument.load(state.pdfArrayBuffer!, { ignoreEncryption: true });

      if (pdfDoc?.isEncrypted) {
        console.error('Failed to add comment: PDF document is encrypted');
        showMessageError({ content: t`PDF document is encrypted` });
        setState({
          isSaving: false,
          isOpenEditReportModal: false,
        });
        return;
      }

      const pageAddComment = 0;

      // *: Need copy new page to prevent text from overlaying
      const copiedPage = pdfDoc.getPage(pageAddComment);

      // *: Register the `fontkit` instance
      pdfDoc.registerFontkit(fontkit);

      // *: Embed our custom font in the document
      const customFont = await pdfDoc.embedFont(fontBytes);

      // *: For testing
      const isTest = false;

      // *: x, y value is according to XY-axis

      // *: Draw white rectangle to hide default comments
      const { drawRectangleOptions, drawTextOptions } = getModifyPdfReportOptions({
        customFont,
        isTest,
        formatVersion,
        aiFormatVersion,
        isNewFormat,
        isNewNotiEventReport,
        isUploadedAiReport: state.isUploadedAiReport,
        isUploadedArcsReport: state.isUploadedArcsReport,
        isInstantReport: state.isInstantReport,
        isNotificationReport: state.isNotificationReport,
        isEventReport: state.isEventReport,
        isInterimReport: state.isInterimReport,
        isEouReport: state.isEouReport,
        isMctReport: state.isMctReport,
        isMctOrMctPeek: state.isMctOrMctPeek,
        isMctOnly: state.isMctOnly,
        profile: data.profile,
      });
      copiedPage.drawRectangle(drawRectangleOptions);

      const cleanedPhysicianInterpretation = physicianInterpretation.replace(/(\r?\n){2,}/g, '\n');
      if (cleanedPhysicianInterpretation) {
        let formatText = cleanedPhysicianInterpretation;
        if (isNewNotiEventReport) {
          formatText = truncateFindingsText(
            cleanedPhysicianInterpretation,
            drawTextOptions,
            customFont,
          );
        }
        copiedPage.drawText(formatText, drawTextOptions);
      }

      if (isNewEventVer200 && data.totalStrips) {
        drawFindingOnEventStrip({
          coverPage: copiedPage,
          totalStrips: data.totalStrips,
          pdfDoc,
          findings: cleanedPhysicianInterpretation,
          customFont,
        });
      }

      // *: Insert edited page as a first page
      pdfDoc.removePage(pageAddComment);
      pdfDoc.insertPage(pageAddComment, copiedPage);

      // Serialize the PDFDocument to bytes (a Uint8Array)
      const pdfBytes = await pdfDoc.save();

      // Convert the Uint8Array to ArrayBuffer
      const pdfArrayBuffer = pdfBytes.buffer.slice(
        pdfBytes.byteOffset,
        pdfBytes.byteOffset + pdfBytes.byteLength,
      ) as ArrayBuffer;

      handleSavePdfReport({
        physicianInterpretation: cleanedPhysicianInterpretation,
        pdfArrayBuffer,
        callback: () => {
          if (_.isFunction(handleAfterSavingPdfReport)) {
            handleAfterSavingPdfReport(data.id);
          }

          setState({
            pdfArrayBuffer,
            arrayBufferChange: new Date().getTime(),
            physicianInterpretation: cleanedPhysicianInterpretation,
            isSaving: false,
            isOpenEditReportModal: false,
          });
        },
      });
    } catch (error) {
      console.error('Failed to add comment: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
      setState({ isSaving: false });
    }
  };

  /**
   * Stamps the physician signature, sign date and findings onto the report PDF and saves it. Requires fontBytes and an unencrypted document, embeds the font, draws findings and event strips, adds the signature PNG with its date, a truncated name and title, refreshes the interpretation block on Study reports, uploads a changed signature via handleSaveSignature, then calls handleSavePdfReport and emits ADD_SIGNATURE_TIME_TRACKING when the tracked total exceeds its limit.
   *
   * @param signatureBase64String - Signature image data url, empty when no signature is added.
   * @param physicianInterpretation - Findings text drawn onto the report.
   * @returns Resolves once the signed PDF was handed to the save step.
   */
  const handleClickAddSignature = async (
    signatureBase64String: string,
    physicianInterpretation: string,
  ) => {
    if (!fontBytes) {
      setState({ openFailedLoadingFontsModal: true });
      return;
    }

    const startTimeInTotal = dayjs();

    setState({ isSaving: true });

    let shouldUpdateSignatureDate = false;

    const startTimeLoadPdf = dayjs();
    // pdfArrayBuffer is guaranteed set: the Add-signature action is only reachable once the report PDF has loaded
    const pdfDoc = await PDFDocument.load(state.pdfArrayBuffer!, { ignoreEncryption: true });
    const endTimeLoadPdf = dayjs();
    addSignatureTrackingTime.current.loadPdf = endTimeLoadPdf.diff(startTimeLoadPdf, 'ms', true);

    if (pdfDoc?.isEncrypted) {
      console.error('Failed to add signature: PDF document is encrypted');
      showMessageError({ content: t`PDF document is encrypted` });
      setState({
        isSaving: false,
        isOpenEditReportModal: false,
      });
      return;
    }

    const pageDrawSignature = 0;

    const startTimeCopyPage = dayjs();
    const copiedPage = pdfDoc.getPage(pageDrawSignature);
    const endTimeCopyPage = dayjs();
    addSignatureTrackingTime.current.copyPage = endTimeCopyPage.diff(startTimeCopyPage, 'ms', true);

    // *: Register the `fontkit` instance
    pdfDoc.registerFontkit(fontkit);

    // *: Embed our custom font in the document
    const startTimeEmbedFont = dayjs();
    const customFont = await pdfDoc.embedFont(fontBytes);
    const endTimeEmbedFont = dayjs();
    addSignatureTrackingTime.current.embedFont = endTimeEmbedFont.diff(
      startTimeEmbedFont,
      'ms',
      true,
    );

    // *: For testing
    const isTest = false;

    // *: Draw white rectangle to hide default comments
    const startTimeDraw = dayjs();
    const { drawRectangleOptions, drawTextOptions } = getModifyPdfReportOptions({
      customFont,
      isTest,
      formatVersion,
      aiFormatVersion,
      isNewFormat,
      isNewNotiEventReport,
      isUploadedAiReport: state.isUploadedAiReport,
      isUploadedArcsReport: state.isUploadedArcsReport,
      isInstantReport: state.isInstantReport,
      isNotificationReport: state.isNotificationReport,
      isEventReport: state.isEventReport,
      isInterimReport: state.isInterimReport,
      isEouReport: state.isEouReport,
      isMctReport: state.isMctReport,
      isMctOrMctPeek: state.isMctOrMctPeek,
      isMctOnly: state.isMctOnly,
      profile: data.profile,
    });
    copiedPage.drawRectangle(drawRectangleOptions);

    const cleanedPhysicianInterpretation = physicianInterpretation.replace(/(\r?\n){2,}/g, '\n');
    if (cleanedPhysicianInterpretation) {
      let formatText = cleanedPhysicianInterpretation;
      if (isNewNotiEventReport) {
        formatText = truncateFindingsText(
          cleanedPhysicianInterpretation,
          drawTextOptions,
          customFont,
        );
      }
      copiedPage.drawText(formatText, drawTextOptions);
    }

    if (isNewEventVer200 && data.totalStrips) {
      drawFindingOnEventStrip({
        coverPage: copiedPage,
        totalStrips: data.totalStrips,
        pdfDoc,
        findings: cleanedPhysicianInterpretation,
        customFont,
      });
    }

    const currentSignTime = dayjs();
    const currentDateTimeText = currentSignTime.format('dddd, MMMM D, YYYY, HH:mm');

    // *: For testing
    // currentDateTimeText = 'Wednesday, January 10, 2024, 15:37';

    if (signatureBase64String) {
      // *: Convert from base64String image to UInt8Array
      const uint8Array = dataURIToUInt8Array(signatureBase64String);

      // *: Embed the JPG image bytes and PNG image bytes
      const pngImage = await pdfDoc.embedPng(uint8Array);

      // *: Get the width/height of the PNG image scaled down to 25% (reduced 400%) of its original size
      // const pngDims = pngImage.scale(0.25);

      const {
        drawImageOptions,
        drawDateRectangleOptions,
        drawDateTextOptions,
        drawTitleTextOptions,
        drawNameTextOptions,
      } = getModifyPdfReportOptions({
        customFont,
        isTest,
        formatVersion,
        aiFormatVersion,
        isNewFormat,
        isNewNotiEventReport,
        isUploadedAiReport: state.isUploadedAiReport,
        isUploadedArcsReport: state.isUploadedArcsReport,
        isInstantReport: state.isInstantReport,
        isNotificationReport: state.isNotificationReport,
        isEventReport: state.isEventReport,
        isInterimReport: state.isInterimReport,
        isEouReport: state.isEouReport,
        isMctReport: state.isMctReport,
        isMctOrMctPeek: state.isMctOrMctPeek,
        isMctOnly: state.isMctOnly,
        profile: data.profile,
      });

      // *: Draw the signature date
      copiedPage.drawRectangle(drawDateRectangleOptions);
      copiedPage.drawText(currentDateTimeText, drawDateTextOptions);
      // *: Draw the title
      if (drawTitleTextOptions) {
        copiedPage.drawText('MD signature:', drawTitleTextOptions);
      }
      // *: Draw the PNG image
      copiedPage.drawImage(pngImage, drawImageOptions);

      // *: Draw name
      if (drawNameTextOptions) {
        const { firstName, lastName, title } = meRedux || {};
        const userInfo = {
          firstName,
          lastName,
        };
        const fullName = generateFullName(userInfo);
        const fontSize = 9;
        const text = title ? `${fullName}, ${title}` : fullName;
        const maxWidth = isNewNotiEventReport
          ? 565
          : (isEoUReport && isNewFormat) || (state.isInterimReport && aiFormatVersion)
            ? 350
            : 270;
        let truncatedText = '';
        for (let i = 0; i < text.length; i += 1) {
          const temp = truncatedText + text[i];
          const width = customFont.widthOfTextAtSize(temp, fontSize);
          if (width > maxWidth) {
            truncatedText += '...';
            break;
          }
          truncatedText = temp;
        }
        copiedPage.drawRectangle({
          x: drawNameTextOptions.x - 1,
          y: drawNameTextOptions.y - 3,
          width: maxWidth + 10,
          height: fontSize + 4,
          color: rgb(1, 1, 1),
          borderColor: rgb(0, 0, 0),
        });
        copiedPage.drawText(truncatedText, drawNameTextOptions);
      }
      if (data?.type === REPORT_TYPE.STUDY) {
        /**
         * The Cera Pro Medium typeface loaded as raw bytes for embedding into the report PDF. Fetches the bundled CeraProMediumFont asset and resolves the response body to an ArrayBuffer.
         *
         * @returns Raw bytes of the medium-weight font file.
         */
        const mediumFontBytes = await fetch(CeraProMediumFont).then((res) => res.arrayBuffer());
        const customFontMedium = await pdfDoc.embedFont(mediumFontBytes);
        handleUpdateInterpretationPdf({
          page: copiedPage,
          customFontMedium,
          customFont,
          formatVersion,
          aiFormatVersion,
          reportType: data?.type,
          facilityInfo: data?.facility,
          isNewFormat,
        });
      }
    }
    const endTimeDraw = dayjs();
    addSignatureTrackingTime.current.draw = endTimeDraw.diff(startTimeDraw, 'ms', true);

    const startTimeRemove = dayjs();
    pdfDoc.removePage(pageDrawSignature);
    const endTimeRemove = dayjs();
    addSignatureTrackingTime.current.removePage = endTimeRemove.diff(startTimeRemove, 'ms', true);

    const startTimeInsert = dayjs();
    pdfDoc.insertPage(pageDrawSignature, copiedPage);
    const endTimeInsert = dayjs();
    addSignatureTrackingTime.current.insertPage = endTimeInsert.diff(startTimeInsert, 'ms', true);

    const startTimeSavePdfDoc = dayjs();
    const pdfBytes = await pdfDoc.save();
    const endTimeSavePdfDoc = dayjs();
    addSignatureTrackingTime.current.savePdfDoc = endTimeSavePdfDoc.diff(
      startTimeSavePdfDoc,
      'ms',
      true,
    );

    // *: Only save signature if checkbox is checked and have a changed signature
    if (signatureBase64String) {
      if (signatureBase64String !== state.signatureBase64String) {
        try {
          const startTimeUploadSignatureToUrl = dayjs();
          const signatureURL = (await uploadFileToUrl(
            [signatureBase64String],
            'png',
            true,
          )) as string;
          const endTimeUploadSignatureToUrl = dayjs();
          addSignatureTrackingTime.current.uploadSignatureToUrl = endTimeUploadSignatureToUrl.diff(
            startTimeUploadSignatureToUrl,
            'ms',
            true,
          );
          if (signatureURL) {
            const startTimeSaveSignature = dayjs();
            const saveSignatureResult = await handleSaveSignature(signatureURL);
            const endTimeSaveSignature = dayjs();
            addSignatureTrackingTime.current.saveSignature = endTimeSaveSignature.diff(
              startTimeSaveSignature,
              'ms',
              true,
            );
            const { data } = saveSignatureResult;
            // handleSaveSignature throws unless data.saveSignature.isSuccess is true, so both are set here
            const { saveSignature } = data!;
            if (saveSignature.isSuccess) {
              hasSignature.current = true;
              shouldUpdateSignatureDate = true;
              updateClickedReport({ signatureBase64String });
            } else {
              console.error('Failed to save signature, message: ', saveSignature.message);
              showMessageError({ content: saveSignature.message });
            }
          }
        } catch (error) {
          console.error('Failed to save signature: ', error);
          showMessageError({ content: error instanceof Error ? error.message : '' });
        }
      } else {
        hasSignature.current = true;
        shouldUpdateSignatureDate = true;
      }
    } else {
      hasSignature.current = false;
      shouldUpdateSignatureDate = false;
    }

    handleSavePdfReport({
      physicianInterpretation: cleanedPhysicianInterpretation,
      signatureBase64String,
      currentSignTime: currentSignTime.toISOString(),
      currentDateTimeText,
      shouldUpdateSignatureDate,
      pdfArrayBuffer: pdfBytes as unknown as ArrayBuffer,
      callback: () => {
        if (_.isFunction(handleAfterSavingPdfReport)) {
          handleAfterSavingPdfReport(data.id);
        }

        setState({
          physicianInterpretation: cleanedPhysicianInterpretation,
          pdfArrayBuffer: pdfBytes as unknown as ArrayBuffer,
          arrayBufferChange: new Date().getTime(),
          signatureBase64String,
          isSaving: false,
          isOpenEditReportModal: false,
        });

        const endTimeInTotal = dayjs();
        addSignatureTrackingTime.current.total = endTimeInTotal.diff(startTimeInTotal, 'ms', true);

        /**
         * Determines whether add-signature timing may be reported for the signed-in user. Calls checkShouldTrackAddSignatureTime, cast to a userId predicate, with auth.userId().
         *
         * @returns True when timing events should be emitted for this user.
         */
        const shouldTrackAddSignatureTime = (
          checkShouldTrackAddSignatureTime as (userId?: string) => boolean
        )(auth.userId());
        if (shouldTrackAddSignatureTime) {
          // *: Send tracking event if total time > 15s
          if (addSignatureTrackingTime.current.total > ADD_SIGNATURE_TIME_TOTAL_LIMIT) {
            emitter.emit(EMITTER_CONSTANTS.ADD_SIGNATURE_TIME_TRACKING, {
              addSignatureTrackingTime: addSignatureTrackingTime.current,
              studyFid: data.studyFid,
              type: data.type,
              isNewFormat: data.isNewFormat,
              reportId: data.id,
              eventFid: data.eventFid,
            });
          }
        }
        addSignatureTrackingTime.current = { ...INITIAL_TIME_DIFF_OBJECT };
      },
    });
  };

  /**
   * Marks the opened inbox report as read. Skips when data.isRead is already true, otherwise calls handleMarkInboxReviewed with the report id and invokes the handleAfterReadingReport callback when one was supplied; a failure surfaces as an error message.
   *
   * @returns Resolves once the report is marked or the call was skipped.
   */
  const handleReadReport = async () => {
    try {
      const { id, isRead } = data;
      if (isRead) {
        return;
      }

      await handleMarkInboxReviewed({
        id,
        isRead: true,
      });

      if (_.isFunction(handleAfterReadingReport)) {
        handleAfterReadingReport({
          id,
          isRead: true,
        });
      }
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }
  };

  /**
   * Accepts edits to the physician interpretation only while the text still fits the report. Runs checkAddPhysicianInterpretation with the current report flags whenever the value grew, showing 'Could not add more lines' on overflow and otherwise storing the text in state.
   *
   * @param name - Field name from the editor; unused.
   * @param value - Edited interpretation text.
   * @returns Nothing.
   */
  const onChangePhysicianInterpretation = (name: string, value: string) => {
    let overflow = false;
    if (value.length > state.physicianInterpretation.length) {
      overflow = checkAddPhysicianInterpretation({
        value,
        isNewFormat,
        formatVersion,
        isNewNotiEventReport,
        isUploadedAiReport: state.isUploadedAiReport,
        isUploadedArcsReport: state.isUploadedArcsReport,
        isInstantReport: state.isInstantReport,
        isEventReport: state.isEventReport,
        isNotificationReport: state.isNotificationReport,
        isInterimReport: state.isInterimReport,
        profile: data.profile,
      });
    }
    if (overflow) {
      showMessageError({ content: t`Could not add more lines` });
    } else {
      setState({ physicianInterpretation: value });
    }
  };

  /**
   * Handles closing the inbox report details view. Opens the unsaved-changes confirmation modal when the physician interpretation was edited, otherwise navigates back immediately.
   *
   * @returns Nothing.
   */
  const handleCloseClick = () => {
    if (hasChangedPhysicianInterpretation) {
      setState({ openUnsavedChangesFindingsModal: true });
    } else {
      handleClickBack();
    }
  };

  /**
   * Leaves the report details page after the unsaved-findings prompt is accepted. Delegates to handleClickBack to navigate away.
   *
   * @returns Nothing.
   */
  const handleLeaveClick = () => {
    handleClickBack();
  };

  /**
   * Keeps the user on the report details page after the unsaved-changes prompt. Sets openUnsavedChangesFindingsModal to false.
   *
   * @returns Nothing.
   */
  const handleStayClick = () => {
    setState({ openUnsavedChangesFindingsModal: false });
  };

  /**
   * Stays on the instant report instead of opening the verified one. Closes the prompt by setting openVerifiedReportModal to false.
   *
   * @returns Nothing.
   */
  const handleViewInstantReportClick = () => {
    setState({ openVerifiedReportModal: false });
  };

  /**
   * Switches from the instant report to its verified version. Closes the verified-report modal and calls the handleViewVerifiedReportClick prop passed by the parent.
   *
   * @returns Nothing.
   */
  const handleViewVerifiedReportClick = () => {
    setState({ openVerifiedReportModal: false });
    handleViewVerifiedReportClickProp();
  };

  /**
   * Dismisses the font-loading failure notice on the report details page. Sets openFailedLoadingFontsModal to false.
   *
   * @returns Nothing.
   */
  const handleGotItClick = () => {
    setState({ openFailedLoadingFontsModal: false });
  };

  useEffect(() => {
    if (!_.isEmpty(data)) {
      setState({ openVerifiedReportModal: hasVerifiedReport });
    }
  }, [data, hasVerifiedReport]);

  useEffect(() => {
    /**
     * The support-store entry matching the report's facility, when that facility is under support. Located by id in supportFacilitiesRedux so its arrears can be emitted to the escalation popup.
     *
     * @returns Matching facility entry, or undefined.
     */
    const foundSelectedFacility = supportFacilitiesRedux?.find((x) => x.id === data.facility?.id);
    if (foundSelectedFacility) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, foundSelectedFacility.arrears);
    }
    if (!isInstantReportProp) {
      handleReadReport();
    }
  }, []);

  useEffect(() => {
    let isEventReport = false;
    let isNotificationReport = false;
    let isEouReport = false;
    let isInterimReport = false;
    let isMctReport = false;
    let isInstantReport = false;
    let isMctOnly = false;
    let isMctOrMctPeek = false;
    let isCardiacEvent = false;
    if (data) {
      const { type, studyType } = data;
      isEouReport = type === 'Study';
      isInterimReport = type === 'Interim';
      isEventReport = type === 'Event';
      isMctReport =
        type !== 'Notification' &&
        (studyType === STUDY_TYPES.MCT || (studyType === STUDY_TYPES.MCT_PEEK && type !== 'Study'));
      isInstantReport = type === 'UNREVIEWED';
      isNotificationReport = type === 'Notification';
      isMctOnly = studyType === STUDY_TYPES.MCT;
      isMctOrMctPeek = [STUDY_TYPES.MCT, STUDY_TYPES.MCT_PEEK].includes(studyType);
      isCardiacEvent = studyType === STUDY_TYPES.CARDIAC_EVENT;
    }
    const isNewNotiEventReport = checkNewNotiEventReport(data);
    const physicianInterpretation =
      (isNewNotiEventReport
        ? data?.reportFindings
        : isInstantReport
          ? (data?.physicianInterpretation ?? data?.technicianComments)
          : data?.physicianInterpretation) || '';
    physInterpretation.current = physicianInterpretation;
    const newState = {
      isEouReport,
      isInterimReport,
      isEventReport,
      isMctReport,
      isInstantReport,
      isMctOnly,
      isNotificationReport,
      isMctOrMctPeek,
      isCardiacEvent,
      physicianInterpretation,
      signatureBase64String: data?.signatureBase64String || '',
      pdfArrayBuffer: data?.pdfArrayBuffer,
    };
    if (!isInitData.current) {
      isInitData.current = true;
      _.assign(newState, { arrayBufferChange: new Date().getTime() });
    }
    setState(newState);
  }, [data]);

  useEffect(() => {
    /**
     * Loads the font used when stamping findings and signatures into the report PDF. For Holter, extended Holter and MCT peek study reports it reads the first page rotation to classify the file as an uploaded AI (0 degrees) or ARCS (90 degrees) report, then fetches CeraPro Regular for new-format, AI, instant, 2.0.0 or profiled interim reports and Tahoma otherwise, reporting a failure to Sentry.
     *
     * @returns A promise resolving once the font bytes and the report-kind flags have landed in state.
     */
    const getFont = async () => {
      let isUploadedAiReport = false;
      let isUploadedArcsReport = false;
      const { type, studyType, profile } = data;
      const isHolterReport =
        type === 'Study' &&
        [STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER, STUDY_TYPES.MCT_PEEK].includes(studyType);
      if (isHolterReport && data?.pdfArrayBuffer) {
        const pdfDoc = await PDFDocument.load(data?.pdfArrayBuffer, { ignoreEncryption: true });
        if (!pdfDoc?.isEncrypted) {
          const firstPage = pdfDoc.getPage(0);
          const pageRotation = firstPage.getRotation();
          isUploadedAiReport = !isNewFormat && pageRotation.angle === 0;
          isUploadedArcsReport = !isNewFormat && pageRotation.angle === 90;
        }
      }
      setState({
        isUploadedAiReport,
        isUploadedArcsReport,
      });

      try {
        const hasProfileInterimReport = type === 'Interim' && !!profile;
        const response = await fetch(
          isNewFormat ||
            isUploadedAiReport ||
            state.isInstantReport ||
            formatVersion >= '2.0.0' ||
            hasProfileInterimReport
            ? CeraProRegularFont
            : TahomaRegularFont,
        );
        if (!response.ok) {
          throw new Error('Failed to load font');
        }
        const arrayBuffer = await response.arrayBuffer();
        setFontBytes(arrayBuffer);
      } catch (error) {
        console.error('Font bytes are not loaded: ', error);
        Sentry.setContext('errorType', {
          errorType: 'Font loading error',
          message: error instanceof Error ? error.message : '',
        });
        Sentry.setContext('reportInfo', {
          id: data?.id,
          reportFid: data?.reportId,
          reportType: data?.type,
          studyId: data?.study?.id,
          studyFid: data?.study?.friendlyId,
          facilityId: data?.facility?.id,
          facilityName: data?.facility?.name,
        });
        Sentry.captureException(error);
        setFontBytes(undefined);
      }
    };

    getFont();
  }, [isNewFormat, formatVersion, data]);

  useEffect(() => {
    let objectURL = '';
    if (state.pdfArrayBuffer) {
      const blob = new Blob([state.pdfArrayBuffer], { type: 'application/pdf' });
      objectURL = URL.createObjectURL(blob);
      const pdfURL = `${objectURL}#view=FitW&zoom=100`;
      setState({ pdfURL });
      if (pdfURL) {
        currentUrl.current = pdfURL;
      }
    }

    return () => {
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [state.arrayBufferChange]);

  const hasProfileInterimReport = state.isInterimReport && !!data?.profile;
  const shouldDisplayFindings =
    isNewFormat || state.isInstantReport || hasProfileInterimReport || isNewNotiEventReport;

  return {
    data,
    navigate,
    state,
    setState,
    isNewFormat,
    formatVersion,
    isNewNotiEventReport,
    hasChangedPhysicianInterpretation,
    toggleDisplayEditReportModal,
    handleSavePdfReport,
    downloadPdfReport,
    handleClickAddComment,
    handleClickAddSignature,
    onChangePhysicianInterpretation,
    handleCloseClick,
    handleLeaveClick,
    handleStayClick,
    handleViewInstantReportClick,
    handleViewVerifiedReportClick,
    handleGotItClick,
    currentUrl,
    shouldDisplayDiagnosticYieldAlert,
    shouldDisplayInstantReportAlert,
    isRendering,
    shouldDisplayFindings,
  };
};

export { useInboxDetails };
